import { FC, useMemo } from 'react';
import { css } from '@emotion/react';
import { getUser, isWeaveUser } from '@frontend/auth-helpers';
import { PaginatedList } from '@frontend/components';
import { useTranslation } from '@frontend/i18n';
import { getFullName } from '@frontend/name';
import { taskCenterTrackingIds } from '@frontend/task-center-components';
import { useTaskCenterStore } from '@frontend/task-center-scope';
import { theme } from '@frontend/theme';
import {
  usePopoverDialog,
  PopoverDialog,
  Avatar,
  Text,
  Button,
  SearchField,
  useFormField,
  useControlledField,
  useDebouncedValue,
  RadioField,
  useListboxState,
  Listbox,
  ListboxOption,
} from '@frontend/design-system';
import {
  buttonStyle,
  listboxStyle,
  optionStyle,
  radioFieldStyle,
  radioStyle,
  searchFieldStyle,
  selfUserContainer,
} from './task-assignee-filters.styles';

interface TaskAssigneeFilterProps {
  setValue: (value: string) => void;
  selectedValue: string;
  getDialogProps: ReturnType<typeof usePopoverDialog>['getDialogProps'];
}

export const TaskAssigneeFilter: FC<TaskAssigneeFilterProps> = ({ setValue, selectedValue, getDialogProps }) => {
  const { t } = useTranslation('task-center');
  const user = getUser();
  const userId = user?.userID;
  const { assignees } = useTaskCenterStore(['assignees']);
  const { onSelect, value, labelId } = useListboxState(selectedValue);
  const weaveUser = isWeaveUser();

  const selfUserName = getFullName(user ?? {});

  const otherAssignees = useMemo(() => {
    return assignees.filter((assignee) => assignee.userId !== userId);
  }, [assignees, userId]);

  const searchFieldProps = useFormField({ type: 'text' });
  const debouncedSearchValue = useDebouncedValue(searchFieldProps.value);
  const radioOptionsProps = useControlledField({
    type: 'radio',
    value: value as string,
    onChange: (value: string) => onSelect(value),
  });

  const filteredAssignees = useMemo(() => {
    if (!debouncedSearchValue.trim().length) return otherAssignees;
    return otherAssignees.filter((assignee) => {
      const name = getFullName(assignee);
      return name.toLowerCase().includes(debouncedSearchValue.toLowerCase());
    });
  }, [debouncedSearchValue, otherAssignees]);

  const onClickApply = () => {
    setValue(value as string);
  };

  return (
    <PopoverDialog {...getDialogProps()} css={popoverStyle}>
      <SearchField name={t('Search Assignee')} placeholder={t('Search')} {...searchFieldProps} css={searchFieldStyle} />
      {/* @ts-expect-error Active prop does exists destructed as rest, but is not defined in component*/}
      <Listbox labelId={labelId} onSelect={onSelect} css={listboxStyle} value={value} active={false}>
        <RadioField name={t('Assignee Filter Options')} {...radioOptionsProps} css={radioStyle}>
          <RadioField.Radio value='' css={radioFieldStyle}>
            {t('All Tasks')}
          </RadioField.Radio>
          {!weaveUser ? (
            <RadioField.Radio value={userId ?? ''} css={radioFieldStyle}>
              <span css={selfUserContainer}>
                <Text>{t('{{username}} (you)', { username: selfUserName })}</Text>
              </span>
            </RadioField.Radio>
          ) : null}
        </RadioField>
        <PaginatedList
          listData={filteredAssignees}
          renderListItem={({ listItem: assignee }) => {
            if (!assignee.userId) return null;
            const name = getFullName(assignee);

            return (
              <ListboxOption key={assignee.userId} value={assignee.userId} css={optionStyle}>
                <Avatar size='xs' name={name} isUser /> <Text size='large'>{name}</Text>
              </ListboxOption>
            );
          }}
          endOfListText={undefined}
        />
      </Listbox>
      <Button
        onClick={onClickApply}
        css={buttonStyle}
        trackingId={taskCenterTrackingIds.filters.applyAssigneeFilterButton}
      >
        {t('Apply')}
      </Button>
    </PopoverDialog>
  );
};

export const popoverStyle = css`
  border-radius: ${theme.borderRadius.medium};
  box-shadow: ${theme.shadows.floating};
  border: 1px solid ${theme.colors.neutral10};
  width: 300px;
`;
